import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import PerformingAssetsCard from "../components/PerformingAssetsCard";
import { InsightSecondaryMetrics } from "../components/InsightMetricLines";
import {
  AGE_RANGE_PLACEHOLDER,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_AGE_RANGE_LABEL,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_HIGHEST_PERFORMING,
} from "../../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../../lib/i18next";
import { META } from "../../../../../utils/constants";
import {
  buildInsightMetricCells,
  getBestPerformanceRow,
  getPerformanceMetricKey,
} from "./MetaDemographicInsightsTables";
import {
  PERFORMING_ASSETS_BLUE,
  PERFORMING_ASSETS_BLUE_SOFT,
  PERFORMING_ASSETS_EYEBROW_SX,
  PERFORMING_ASSETS_HAIRLINE,
} from "../components/performingAssetsStyles";
import { finiteMetric } from "../components/performingAssetsMetrics";

const AgeColumnChart = ({ rows, highlight, metricKey, maxValue }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-end",
      gap: 0.75,
      height: 220,
      mt: 2,
    }}
  >
    {rows.map((row) => {
      const value = finiteMetric(row?.[metricKey]) ?? 0;
      const ratio = maxValue > 0 ? value / maxValue : 0;
      const height = `${Math.max(8, ratio * 100)}%`;
      const isHighlight = row === highlight;
      return (
        <Box
          key={row.age}
          sx={{
            flex: 1,
            minWidth: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: "72%",
              height,
              borderRadius: "6px 6px 2px 2px",
              bgcolor: isHighlight
                ? PERFORMING_ASSETS_BLUE
                : PERFORMING_ASSETS_BLUE_SOFT,
            }}
          />
          <Typography
            component="span"
            sx={{
              mt: 0.5,
              fontSize: "0.625rem",
              color: "fields.placeholder",
              whiteSpace: "nowrap",
            }}
          >
            {row.age}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const AgeMiniTable = ({ rows, highlight, getCostByMainCurrency }) => {
  const { t } = useTranslationApp();

  return (
    <Box sx={{ minWidth: 0 }}>
      {rows.map((row) => {
        const cells = buildInsightMetricCells({
          row,
          t,
          getCostByMainCurrency,
        });
        return (
          <Box
            key={row.age}
            sx={{
              py: 0.75,
              borderTop: `1px solid ${PERFORMING_ASSETS_HAIRLINE}`,
            }}
          >
            <Typography component="p" sx={PERFORMING_ASSETS_EYEBROW_SX}>
              {t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_AGE_RANGE_LABEL)}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: row === highlight ? 700 : 600,
                color:
                  row === highlight ? PERFORMING_ASSETS_BLUE : "text.primary",
              }}
            >
              {row.age}
            </Typography>
            <InsightSecondaryMetrics items={cells} singleRow />
          </Box>
        );
      })}
    </Box>
  );
};

const MetaAgeDemographicInsightsCard = ({
  ageRows = [],
  getCostByMainCurrency,
}) => {
  const { t } = useTranslationApp();
  const metricKey = getPerformanceMetricKey(ageRows);
  const highlight = getBestPerformanceRow(ageRows);
  const maxValue = ageRows.reduce(
    (max, row) => Math.max(max, finiteMetric(row?.[metricKey]) ?? 0),
    0
  );

  if (!ageRows?.length) return null;

  return (
    <PerformingAssetsCard
      platform={META}
      title={t(AGE_RANGE_PLACEHOLDER)}
      nextContent={
        <Box>
          {highlight && maxValue > 0 ? (
            <Box sx={{ mb: 1 }}>
              <Typography
                component="p"
                sx={{
                  m: 0,
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  color: PERFORMING_ASSETS_BLUE,
                  lineHeight: 1.2,
                }}
              >
                {highlight.age}
              </Typography>
              <Typography
                component="p"
                sx={{ ...PERFORMING_ASSETS_EYEBROW_SX, mt: 0.25 }}
              >
                {t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_HIGHEST_PERFORMING)}
              </Typography>
            </Box>
          ) : null}
          <Box
            data-pdf-age-layout
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2.5,
              alignItems: "flex-end",
            }}
          >
            <Box sx={{ flex: "2 1 240px", minWidth: 0 }}>
              {highlight && maxValue > 0 ? (
                <AgeColumnChart
                  rows={ageRows}
                  highlight={highlight}
                  metricKey={metricKey}
                  maxValue={maxValue}
                />
              ) : null}
            </Box>
            <Box sx={{ flex: "3 1 320px", minWidth: 0 }}>
              <AgeMiniTable
                rows={ageRows}
                highlight={highlight}
                getCostByMainCurrency={getCostByMainCurrency}
              />
            </Box>
          </Box>
        </Box>
      }
    />
  );
};

export default MetaAgeDemographicInsightsCard;
